<script setup>
import { $api } from '@/services';
import { ref, watch, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { getStudent } from '@/features/student/useStudent';
import { _ROUTER_NAME } from '@/enums';
import { _GET_DATE_2 } from '@/helper/formatDate';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import ResultModal from '@/views/record/studentDetail/features/plan/ResultModal.vue';
import router from '@/router';
import Loading from '@/components/common/Loading';
import CreateResult from '@/views/record/studentDetail/features/plan/CreateResult.vue';
import { useStore } from 'vuex';
import ConfirmSend from '@/views/planPreview/ConfirmSend.vue';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';
import GoalDetail from '@/views/goal/GoalDetail.vue';
import TooltipGoalDetail from '@/components/elements/tooltipGoalDetail/TooltipGoalDetail';
import Multiselect from '@suadelabs/vue3-multiselect';
import Popper from 'vue3-popper';

const store = useStore();
const route = useRoute();
const isShow = ref(false);
const type = ref('');
const loading = ref(false);
const empty = ref(false);
const data = ref([]);
const statistics = ref([]);
const result = ref([]);
const emotion_cooperation_result_by_day = ref([]);
const startDate = ref('');
const endDate = ref('');
const student = ref(null);
const profileStudent = ref(null);
const range = ref(15);
const unitType = ref(['tháng tuổi', 'tuổi']);
const resultId = ref(null);
const aiUsed = ref(false);
const chartOptions = ref({
	dataLabels: {
		enabled: false,
	},
	colors: ['#7AD2C6', '#93BF56', '#F8BB54', '#F47C7C', '#E5E7EB'],
	labels: [
		'Đạt 80 - 100%',
		'Đạt 55 - 75%',
		'Đạt 30 - 50%',
		'Đạt 5 - 25%',
		'X',
	],
	legend: {
		show: false,
	},
	tooltip: {
		fillSeriesColor: true,
		y: {
			formatter: function (value) {
				return value + ' mục tiêu';
			},
		},
	},
	stroke: {
		lineCap: 'round',
	},
});
const chartOptions3 = ref({
	colors: ['#9EDF93', '#D0DB54', '#FFE68C', '#FF9D6D', '#FF6967'],
	chart: {
		width: 380,
		type: 'polarArea',
	},
	labels: ['Vui vẻ', 'Bình thường', 'Khó chịu', 'Tức giận', 'Bùng nổ'],
	fill: {
		opacity: 1,
		colors: ['#9EDF93', '#D0DB54', '#FFE68C', '#FF9D6D', '#FF6967'],
	},
	stroke: {
		lineCap: 'round',
	},
	yaxis: {
		show: false,
	},
	tooltip: {
		fillSeriesColor: true,
		y: {
			formatter: function (value) {
				if (!value || value == 'null') {
					return 'Không thực hiện';
				}
				return value + '%';
			},
		},
	},
	legend: {
		show: false,
	},
	dataLabels: {
		enabled: false,
	},
	plotOptions: {
		polarArea: {
			rings: {
				strokeWidth: 0,
			},
			spokes: {
				strokeWidth: 0,
			},
		},
	},
});
const mixChartOptions = ref({
	chart: {
		type: 'line',
		stacked: false,
	},
	dataLabels: {
		enabled: true,
		enabledOnSeries: [0, 1],
		offsetY: -10,
		background: {
			enabled: true,
			foreColor: '#334155',
		},
		formatter: function (val) {
			return val + '%';
		},
	},
	stroke: {
		width: [0.5, 0.5, 4],
	},
	yaxis: [
		{
			opposite: false,
			axisTicks: {
				show: false,
			},
			axisBorder: {
				show: false,
			},
			labels: {
				show: false,
				formatter: function (value) {
					if (!value || value == 'null') {
						return 'Không thực hiện';
					}
					return value + '%';
				},
			},
			title: {
				show: false,
			},
			max: 100,
		},
		{
			opposite: false,
			axisTicks: {
				show: false,
			},
			axisBorder: {
				show: false,
			},
			labels: {
				show: false,
				formatter: function (value) {
					if (!value || value == 'null') {
						return 'Không thực hiện';
					}
					return value + '%';
				},
			},
			title: {
				show: false,
			},
			max: 100,
		},
		{
			opposite: false,
			axisTicks: {
				show: false,
			},
			axisBorder: {
				show: false,
			},
			labels: {
				show: false,
				formatter: function (value) {
					if (!value || value == 'null') {
						return 'Không thực hiện';
					}
					return value + '%';
				},
			},
			title: {
				show: false,
			},
			max: 100,
		},
	],
	tooltip: {
		fixed: {
			enabled: true,
			position: 'topLeft', // topRight, topLeft, bottomRight, bottomLeft
			offsetY: 30,
			offsetX: 60,
		},
	},
	legend: {
		horizontalAlign: 'left',
		offsetX: 40,
	},
	plotOptions: {
		bar: {
			horizontal: false,
			columnWidth: '20%',
			borderRadius: 4,
		},
	},
	labels: [],
	colors: ['#FFD270', '#DA8F8F', '#6ED0B9'],
	markers: {
		size: 8,
		strokeColors: '#fff',
		strokeWidth: 2,
	},
});
const emotion_cooperation_result_by_day_data = ref([
	{
		name: 'Cảm xúc',
		type: 'column',
		data: [0, 0, 0, 0, 0],
	},
	{
		name: 'Sự tham gia',
		type: 'column',
		data: [0, 0, 0, 0, 0],
	},
	{
		name: 'Kết quả buổi học',
		type: 'line',
		data: [0, 0, 0, 0, 0],
	},
]);
const emotion_cooperation_result_by_day_emotion = ref([]);
const emotion_cooperation_result_by_day_rating = ref([]);
const emotion_cooperation_result_by_day_result = ref([]);
const emotion_cooperation_result_by_day_labels = ref([]);
const mixChart = ref(null);
const radarChartOptions = ref({
	chart: {
		id: 'radarChart',
		type: 'radar',
	},
	title: {
		show: false,
	},
	stroke: {
		width: 0,
	},
	fill: {
		opacity: 0.2,
		colors: ['#65A30D'],
	},
	markers: {
		size: 5,
		hover: {
			size: 10,
		},
		colors: ['#65A30D'],
	},
	dataLabels: {
		enabled: true,
		formatter: function (val) {
			return val + '%';
		},
		style: {
			fontFamily: 'Gilrory',
			fontSize: '20px',
			fontWeight: 'bold',
			colors: ['#344256'],
		},
		background: {
			enabled: false,
		},
	},
	yaxis: {
		show: false,
		logBase: 100,
		tickAmount: 10,
		min: 0,
		max: 100,
		labels: {
			show: false,
		},
	},
	xaxis: {
		categories: [],
		labels: {
			show: true,
			style: {
				fontFamily: 'Gilrory',
				fontSize: '14px',
				fontWeight: 600,
				colors: Array(1000).fill('#344256'),
			},
			formatter: function (val, index) {
				return index?.dataPointIndex + 1;
			},
		},
	},
	tooltip: {
		x: {
			show: true,
			formatter: function (val, object) {
				return `${object?.dataPointIndex + 1}: ${val}`;
			},
			title: {
				formatter: (seriesName) => seriesName,
			},
		},
		y: {
			show: true,
			formatter: function (val) {
				return val + '%';
			},
			title: {
				title: {
					formatter: (seriesName) => seriesName + ':',
				},
			},
		},
	},
});
const series = ref([]);
const series3 = ref([]);
const radarData = ref([]);
const categories = ref([]);
const recommend = ref('');
const note = ref('');
const editMode = ref(false);
const editGoal = ref(false);
const goalIndex = ref(null);
const assessment_results = ref([]);
const skills = ref([]);
const developmentAge = ref([]);
const enableAiButton = computed(() => store.state.profile.enable_ai_button);
const org = computed(() => store.state.profile.current_org);
const isOpenModal = ref(false);
const goalId = ref(null);
const studentId = ref(route.params.id);
const ageLine = computed(() => {
	const profile = profileStudent.value;
	if (!profile?.age) return '';
	const inactive = [
		'graduated',
		'closed',
		'transfered',
		'stopped',
		'pending',
	];
	if (profile.learning_time && !inactive.includes(profile.state)) {
		return `${profile.age} | ${profile.learning_time}`;
	}
	return profile.age;
});

onMounted(async () => {
	const res = await getStudent(String(route.params.id));
	profileStudent.value = res?.data || null;
});

function exitProcess() {
	router.push({ name: _ROUTER_NAME.PROCESS_ASSESSMENT });
}
const resultData = ref({
	name: '',
	assessment_date: null,
	note: '',
	prescription: [],
	development_ages: [],
	assessment_conclusions: [],
});
const skillsWithAge = ref([]);
const create = ref(false);

watch([startDate, endDate], () => {
	if (startDate.value && endDate.value) fetchPeriodsReport();
});

watch(
	mixChart,
	() => {
		const optimalColumnWidthPercent =
			12 +
			16 /
				(1 +
					30 *
						Math.exp(
							-emotion_cooperation_result_by_day_emotion.value
								?.length / 3
						));

		setTimeout(() => {
			mixChart.value?.updateOptions({
				plotOptions: {
					bar: {
						columnWidth: optimalColumnWidthPercent + '%',
					},
				},
			});
		}, 1000);
	},
	{ immediate: true, deep: true }
);

watch(
	skills,
	(value) => {
		value.forEach((v) => {
			if (v.age && !v.unit) {
				v.unit = 'tháng tuổi';
			}
		});
		skillsWithAge.value = value.filter((i) => i.age);
	},
	{ deep: true }
);

const formatDate = (dateString, reverse) => {
	const date = new Date(dateString);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	let formattedDate;
	if (reverse) formattedDate = `${day}/${month}/${year}`;
	else formattedDate = `${year}-${month}-${day}`;
	return formattedDate;
};
const fetchPeriodsReport = async () => {
	loading.value = true;
	try {
		emotion_cooperation_result_by_day.value = [];
		emotion_cooperation_result_by_day_emotion.value = [];
		emotion_cooperation_result_by_day_rating.value = [];
		emotion_cooperation_result_by_day_result.value = [];
		emotion_cooperation_result_by_day_labels.value = [];
		emotion_cooperation_result_by_day_data.value = [
			{
				name: 'Kết quả buổi học',
				type: 'line',
				data: [0, 0, 0, 0, 0],
			},
			{
				name: 'Sự tham gia',
				type: 'column',
				data: [0, 0, 0, 0, 0],
			},
			{
				name: 'Cảm xúc',
				type: 'column',
				data: [0, 0, 0, 0, 0],
			},
		];
		const res = await $api.monthlyReport.periodReports(
			route.params.id,
			formatDate(startDate.value),
			formatDate(endDate.value)
		);
		if (res) {
			data.value = res.data;
			statistics.value = res.data.data;
			student.value = res.data.student_and_assessment_conclusion;
			range.value = getAge(student.value?.student?.age) + 2;
			if (
				statistics.value?.goal_count === 0 &&
				data.value?.stats?.attendance === 0
			) {
				empty.value = true;
				loading.value = false;
				return;
			}
			emotion_cooperation_result_by_day.value =
				res.data.stats?.emotion_cooperation_result_by_day;
			assessment_results.value = res.data.skills;
			let a = [];
			let count = 0;
			result.value = res.data.stats?.result;
			result.value.forEach((item) => {
				if (
					item.result === 'Từ chối' ||
					item.result === 'Không có cơ hội' ||
					item.result === 'Không hợp tác'
				) {
					count += item.count;
				} else {
					a.push(item);
				}
			});
			a.push({
				result: 'X',
				count: count,
			});
			series.value = a.map((item) => {
				return item.count;
			});
			let series3Total = 0;
			res.data.stats?.emotion_scale.forEach((item) => {
				return (series3Total += item.count);
			});
			series3.value = res.data.stats?.emotion_scale.map((item) => {
				const r = (item.count * 100) / series3Total;
				return Math.round(r * 10) / 10 || 0;
			});
			emotion_cooperation_result_by_day.value.forEach((item) => {
				emotion_cooperation_result_by_day_emotion.value.push(
					+(+item.emotion_scale / 5) * 100 || null
				);
				emotion_cooperation_result_by_day_rating.value.push(
					+(+item.cooperation_scale / 5) * 100 || null
				);
				emotion_cooperation_result_by_day_result.value.push(
					+item.complete_percent || null
				);
				emotion_cooperation_result_by_day_labels.value.push(
					_GET_DATE_2(item.date, true)
				);
			});
			emotion_cooperation_result_by_day_data.value[1] = {
				name: 'Cảm xúc',
				type: 'column',
				data: emotion_cooperation_result_by_day_emotion.value,
			};
			emotion_cooperation_result_by_day_data.value[0] = {
				name: 'Sự tham gia',
				type: 'column',
				data: emotion_cooperation_result_by_day_rating.value,
			};
			emotion_cooperation_result_by_day_data.value[2] = {
				name: 'Kết quả buổi học',
				type: 'line',
				data: emotion_cooperation_result_by_day_result.value,
			};
			const optimalColumnWidthPercent =
				5 +
				15 /
					(1 +
						30 *
							Math.exp(
								-emotion_cooperation_result_by_day_labels.value
									?.length / 3
							));

			setTimeout(() => {
				mixChart.value?.updateOptions({
					labels: emotion_cooperation_result_by_day_labels.value,
					plotOptions: {
						bar: {
							columnWidth: optimalColumnWidthPercent + '%',
						},
					},
				});
			}, 1000);
			if (data.value.skills.length) {
				radarData.value = [
					{
						name: 'Kết quả',
						data: data.value.skills.map((skill) => {
							return skill?.skill?.skill_percent;
						}),
					},
				];
				radarChartOptions.value.xaxis.categories =
					data.value.skills.map((skill) => {
						return skill?.skill?.name;
					});
				categories.value = data.value.skills.map((skill) => {
					return skill?.skill?.name;
				});
			}
		}
	} catch (e) {
		console.log(e);
	} finally {
		loading.value = false;
	}
};
function updateDevelopmentAge() {
	developmentAge.value = skills.value
		.filter((i) => i.age)
		.map((s) => `${s.name || s.skill?.name} ${s.age} ${s.unit}`);
}

const sendReport = async () => {
	loading.value = true;
	try {
		let skill =
			skills.value.length > 0 ? skills.value : assessment_results.value;
		let listHideId = [];
		listHideId = skill
			.flatMap((skill) => skill.goals)
			.filter((goal) => goal.show)
			.map((goal) => goal.goal_id);
		if (skills.value.length > 0) {
			skills.value.forEach((s) => {
				if (s.unit === 'tuổi' && s.age < 6) {
					s.age = s.age * 12;
					s.unit = 'tháng tuổi';
				}
			});
			updateDevelopmentAge();
			if (resultData.value) {
				resultData.value.development_ages = [...developmentAge.value];
			}
		}
		isOpenModal.value = false;
		const res = await $api.student.sendReport(Number(route.params.id), {
			send: create.value,

			assessment_document: {
				...resultData.value,
				prescription: resultData.value.prescription?.filter(
					(item) => !!item
				),
				development_age:
					skills.value.filter((s) => s.age).length > 0
						? developmentAge.value
						: resultData.value.development_ages?.filter(
								(item) => !!item
						  ),
				assessment_conclusion:
					resultData.value.assessment_conclusions?.filter(
						(item) => !!item
					),
				id: undefined,
			},
			start_date: formatDate(startDate.value),
			end_date: formatDate(endDate.value),
			period_report: {
				recomended_for_parent: recommend.value,
				note: note.value,
				skill_notes_and_ages: skills.value.map(
					({ goals, skill, ...rest }) => rest
				),
				hidden_goal_ids: listHideId,
			},
		});
		if (res.data.data.assessment_document?.id) {
			resultId.value = res.data.data.assessment_document.id;
		}
		await store.dispatch('setAlertMessage', {
			message: create.value
				? 'Gửi tới phụ huynh thành công'
				: 'Lưu bản nháp thành công',
		});
		if (share.value) {
			shareReport().then(() => {
				shareAssessmentResult().then(() => {
					if (reportToken.value && assessmentResultToken.value) {
						window.open(
							`/pdf/record/student/${route.params.id}/plan?report_token=${reportToken.value}&assessment_result_token=${assessmentResultToken.value}`,
							'_blank'
						);
					}
				});
			});
			share.value = false;
			return;
		}
		router.push(`/record/student/${route.params.id}/result`);
	} catch (e) {
		store.dispatch('setAlertMessage', {
			message:
				e?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		loading.value = false;
	}
};
function openModal(result) {
	resultData.value = result;
	isShow.value = false;
	if (create.value) isOpenModal.value = true;
	else sendReport();
}

function openPreview(val) {
	create.value = val;
	type.value = 'preview';
	isShow.value = true;
}
function openCreate() {
	type.value = 'create';
	isShow.value = true;
}
function submit(data) {
	isShow.value = false;
	// fetchPeriodsReport();
	note.value = data?.note;
	recommend.value = data?.recomended_for_parent;
	skills.value = assessment_results.value.map((item) => {
		const match = data?.skill_notes_and_ages.find(
			(i) => i.id === item?.skill?.id
		);
		return { ...item, ...match };
	});
	skillsWithAge.value = skills.value.filter((i) => i.age);
	updateDevelopmentAge();
	aiUsed.value = true;
}

function formatValue(value) {
	if (value < 72) {
		return value + 'th';
	} else {
		const years = Math.floor(value / 12);
		return years + 'T';
	}
}

const formattedValues = computed(() => {
	const unique = new Set();
	const result = [];
	for (let i = 1; i <= range.value; i++) {
		const value = i * 6 - 6;
		const formatted = formatValue(value);
		if (!unique.has(formatted)) {
			unique.add(formatted);
			result.push(formatted);
		}
	}
	return result;
});

function getAge(data) {
	if (data) {
		data = data.toLowerCase().trim();
		if (data.includes('tuổi') && data.includes('tháng')) {
			const ageMatch = data.match(/(\d+)\s*tuổi\s*(\d*)\s*tháng?/);
			if (ageMatch) {
				const years = parseInt(ageMatch[1], 10);
				const months = parseInt(ageMatch[2] || '0', 10);
				return years * 2 + (months >= 6 ? 1 : 0);
			}
		} else if (data.includes('tuổi')) {
			const match = data.match(/(\d+)\s*tuổi/);
			if (match) {
				const years = parseInt(match[1], 10);
				return years * 2;
			}
		}
	} else return 15;
}

function parseAgeToMonths(ageString) {
	const yearMatch = ageString.match(/(\d+)\s*tuổi/);
	const monthMatch = ageString.match(/(\d+)\s*tháng/);
	const years = yearMatch ? parseInt(yearMatch[1]) : 0;
	const months = monthMatch ? parseInt(monthMatch[1]) : 0;
	return years * 12 + months;
}

function edit(data) {
	goalIndex.value = data;
	editGoal.value = true;
}
function save() {
	updateDevelopmentAge();
	goalIndex.value = null;
	editGoal.value = false;
}

function openGoalDetail(id) {
	goalId.value = id;
	type.value = 'detail';
	isShow.value = true;
}

function getAgeWidth(ageInMonths) {
	const basePx = 64;

	if (ageInMonths <= 72) {
		return (ageInMonths / 6) * basePx + 'px';
	} else {
		const first72 = (72 / 6) * basePx;
		const extraMonths = ageInMonths - 72;
		const extraYears = extraMonths / 12;
		return first72 + extraYears * basePx + 'px';
	}
}
const share = ref(false);

const handleShare = () => {
	share.value = true;
	openPreview(false);
};
const reportToken = ref(null);
const assessmentResultToken = ref(null);
async function shareReport() {
	try {
		const res = await $api.monthlyReport.shareReport(
			Number(route.params.id),
			formatDate(startDate.value),
			formatDate(endDate.value)
		);
		if (res.data.data.token) {
			reportToken.value = res.data.data.token;
		} else {
			await store.dispatch('setAlertMessage', {
				message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
				type: 'danger',
			});
		}
	} catch (error) {
		console.log(error);
	}
}
async function shareAssessmentResult() {
	try {
		const res = await $api.monthlyReport.shareAssessmentResult(
			Number(route.params.id),
			resultId.value
		);
		if (res.data.data.token) {
			assessmentResultToken.value = res.data.data.token;
		} else {
			await store.dispatch('setAlertMessage', {
				message: 'Đã có lỗi xảy ra. Vui lòng thử lại',
				type: 'danger',
			});
		}
	} catch (error) {
		console.log(error);
	}
}
</script>
<template>
	<div :class="isShow ? 'side-over--active' : ''">
		<div
			v-if="isShow"
			class="fixed h-screen w-screen top-0 left-0 bg-black opacity-20 z-30"
			@click="isShow = false"
		></div>
		<ResultModal
			v-if="type === 'preview'"
			v-model:refresh="isShow"
			@close="isShow = false"
			@to-preview="openModal"
			:developmentAges="developmentAge"
			:assessmentConclusions="student?.assessment_conclusions"
			:note="note"
		/>
		<CreateResult
			v-if="type === 'create'"
			v-model:refresh="isShow"
			@close="isShow = false"
			@submit="submit"
			:student="student"
			:startDate="formatDate(startDate)"
			:endDate="formatDate(endDate)"
			:create="create"
			:isShow="isShow"
		/>
		<GoalDetail
			v-if="type === 'detail'"
			@close="isShow = false"
			:goalId="goalId"
			:sideOverShow="isShow"
			:studentId="studentId"
		/>
	</div>
	<DaModalFrame v-model:open="isOpenModal">
		<ConfirmSend @close="isOpenModal = false" @submit="sendReport" />
	</DaModalFrame>
	<div class="px-10 pt-8 pb-16 md-down:px-4">
		<div class="flex items-center justify-between">
			<div class="text-sm font-semibold text-neutral-500">
				Đánh giá quá trình can thiệp
			</div>
			<button
				class="
					button-p
					text-center
					w-fit
					btn--text
					text-neutral-500
					hover:text-neutral-600
				"
				type="button"
				@click="exitProcess"
			>
				<svg
					class="mr-2 text-neutral-400"
					fill="none"
					height="16"
					viewBox="0 0 16 16"
					width="16"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M1.5 7.99996L0.719131 7.37526L0.219375 7.99996L0.719131 8.62465L1.5 7.99996ZM7.5 8.99996C8.05228 8.99996 8.5 8.55224 8.5 7.99996C8.5 7.44767 8.05228 6.99996 7.5 6.99996V8.99996ZM3.3858 4.04193L0.719131 7.37526L2.28087 8.62465L4.94754 5.29132L3.3858 4.04193ZM0.719131 8.62465L3.3858 11.958L4.94754 10.7086L2.28087 7.37526L0.719131 8.62465ZM1.5 8.99996H7.5V6.99996H1.5V8.99996Z"
						fill="currentColor"
					/>
					<path
						d="M7.78738 13.3439L8.31032 12.4915L7.78738 13.3439ZM6.97976 12.3905L6.05301 12.7662L6.97976 12.3905ZM14.499 12.5015L15.3875 12.9603L14.499 12.5015ZM12.7525 13.981L12.4461 13.0291L12.7525 13.981ZM14.499 3.49854L15.3875 3.0398L14.499 3.49854ZM13.8333 6.81921V9.18087H15.8333V6.81921H13.8333ZM7.83325 5.42133V5.29122H5.83325V5.42133H7.83325ZM9.58524 12.7782C8.62733 12.6186 8.43551 12.5683 8.31032 12.4915L7.26445 14.1962C7.80406 14.5273 8.47961 14.6216 9.25644 14.751L9.58524 12.7782ZM5.83325 10.7107C5.83325 11.4979 5.81518 12.1795 6.05301 12.7662L7.90651 12.0148C7.85132 11.8787 7.83325 11.6814 7.83325 10.7107H5.83325ZM8.31032 12.4915C8.12826 12.3798 7.98675 12.2128 7.90651 12.0148L6.05301 12.7662C6.29374 13.36 6.71828 13.8612 7.26445 14.1962L8.31032 12.4915ZM13.8333 9.18087C13.8333 10.0882 13.8325 10.7099 13.7946 11.1876C13.7573 11.6573 13.6894 11.8898 13.6104 12.0428L15.3875 12.9603C15.6428 12.4658 15.742 11.929 15.7883 11.3458C15.834 10.7705 15.8333 10.0557 15.8333 9.18087H13.8333ZM9.25644 14.751C10.1198 14.8949 10.8251 15.0132 11.4002 15.0628C11.9832 15.113 12.5291 15.1035 13.0589 14.9329L12.4461 13.0291C12.2822 13.0819 12.0416 13.1107 11.5719 13.0702C11.0943 13.029 10.4806 12.9275 9.58524 12.7782L9.25644 14.751ZM13.6104 12.0428C13.3679 12.5125 12.9493 12.8671 12.4461 13.0291L13.0589 14.9329C14.0653 14.609 14.9025 13.8997 15.3875 12.9603L13.6104 12.0428ZM9.58645 3.22163C10.4814 3.07248 11.0948 2.97099 11.5722 2.92986C12.0416 2.88942 12.2822 2.91819 12.4461 2.97096L13.0589 1.06717C12.5292 0.896646 11.9834 0.887021 11.4005 0.937243C10.8256 0.986778 10.1206 1.10503 9.25766 1.24885L9.58645 3.22163ZM15.8333 6.81921C15.8333 5.9444 15.834 5.22954 15.7883 4.65428C15.742 4.07104 15.6428 3.53429 15.3875 3.0398L13.6104 3.95728C13.6894 4.1103 13.7573 4.34283 13.7946 4.81252C13.8325 5.29019 13.8333 5.91191 13.8333 6.81921H15.8333ZM12.4461 2.97096C12.9493 3.13294 13.3679 3.48755 13.6104 3.95728L15.3875 3.0398C14.9025 2.10035 14.0653 1.39113 13.0589 1.06717L12.4461 2.97096ZM9.25766 1.24885C8.83685 1.31898 8.47124 1.37935 8.17099 1.45102C7.85987 1.52528 7.55473 1.62545 7.26252 1.80503L8.30967 3.50898C8.35038 3.48397 8.42887 3.44565 8.63532 3.39637C8.85265 3.3445 9.13857 3.29628 9.58645 3.22163L9.25766 1.24885ZM7.83325 5.29122C7.83325 4.83715 7.83381 4.5472 7.84925 4.3243C7.86391 4.11256 7.88881 4.02884 7.90679 3.98457L6.05387 3.23179C5.92477 3.54955 5.87613 3.86701 5.85403 4.1861C5.8327 4.49405 5.83325 4.86461 5.83325 5.29122H7.83325ZM7.26252 1.80503C6.71792 2.13971 6.29446 2.63958 6.05387 3.23179L7.90679 3.98457C7.98699 3.78717 8.12814 3.62054 8.30967 3.50898L7.26252 1.80503Z"
						fill="currentColor"
					/>
				</svg>
				Thoát
			</button>
		</div>
		<div v-if="profileStudent" class="flex items-center pt-5 pb-6">
			<img
				v-if="profileStudent.profile_photo?.w200"
				:alt="profileStudent.name"
				:src="profileStudent.profile_photo.w200"
				class="shadow-sm inline-block h-14 w-14 rounded-full"
				style="min-width: 56px"
			/>
			<span
				v-else
				:style="{
					background: profileStudent.profile_photo?.default?.color,
				}"
				class="
					inline-flex
					items-center
					justify-center
					h-14
					w-14
					rounded-full
				"
				style="min-width: 56px"
			>
				<span class="font-medium leading-none text-white text-4xl">{{
					profileStudent.profile_photo?.default?.initial
				}}</span>
			</span>
			<div class="ml-4">
				<div class="font-bold text-neutral-700 text-2xl">
					{{ profileStudent.name }}
				</div>
				<div
					v-if="ageLine"
					class="
						text-green-500
						font-semibold
						text-xs
						uppercase
						letter-spacing-8
					"
				>
					{{ ageLine }}
				</div>
			</div>
		</div>
		<div class="search-input w-ful flex md-down:flex-col gap-6">
			<div style="width: 200px">
				<div class="input-group__label">Từ ngày</div>
				<div class="input-group">
					<VueDatepicker
						v-model="startDate"
						:enableTimePicker="false"
						:max-date="endDate ? new Date(endDate) : new Date()"
						autoApply
						class="input-date w-full"
						format="dd/MM/yyyy"
						minutesIncrement="30"
						placeholder="Chọn ngày"
						uid="filter-by-date"
					>
						<template #input-icon>
							<svg
								fill="none"
								height="20"
								viewBox="0 0 20 20"
								width="20"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									clip-rule="evenodd"
									d="M5 0.625C5 0.279822 4.72018 0 4.375 0C4.02982 0 3.75 0.279822 3.75 0.625V1.25H2.5C1.11929 1.25 0 2.36929 0 3.75V5H20V3.75C20 2.36929 18.8807 1.25 17.5 1.25H16.25V0.625C16.25 0.279822 15.9702 0 15.625 0C15.2798 0 15 0.279822 15 0.625V1.25H5V0.625ZM20 17.5V6.25H0V17.5C0 18.8807 1.11929 20 2.5 20H17.5C18.8807 20 20 18.8807 20 17.5ZM11.875 8.75C11.5298 8.75 11.25 9.02982 11.25 9.375V10.625C11.25 10.9702 11.5298 11.25 11.875 11.25H13.125C13.4702 11.25 13.75 10.9702 13.75 10.625V9.375C13.75 9.02982 13.4702 8.75 13.125 8.75H11.875ZM15.625 8.75C15.2798 8.75 15 9.02982 15 9.375V10.625C15 10.9702 15.2798 11.25 15.625 11.25H16.875C17.2202 11.25 17.5 10.9702 17.5 10.625V9.375C17.5 9.02982 17.2202 8.75 16.875 8.75H15.625ZM2.5 13.125C2.5 12.7798 2.77982 12.5 3.125 12.5H4.375C4.72018 12.5 5 12.7798 5 13.125V14.375C5 14.7202 4.72018 15 4.375 15H3.125C2.77982 15 2.5 14.7202 2.5 14.375V13.125ZM6.875 12.5C6.52982 12.5 6.25 12.7798 6.25 13.125V14.375C6.25 14.7202 6.52982 15 6.875 15H8.125C8.47018 15 8.75 14.7202 8.75 14.375V13.125C8.75 12.7798 8.47018 12.5 8.125 12.5H6.875Z"
									fill="#94A3B8"
									fill-rule="evenodd"
								/>
							</svg>
						</template>
					</VueDatepicker>
				</div>
			</div>
			<div style="width: 200px">
				<div class="input-group__label">Tới ngày</div>
				<div class="input-group">
					<VueDatepicker
						v-model="endDate"
						:disabled="!startDate"
						:enableTimePicker="false"
						:max-date="new Date()"
						:min-date="new Date(startDate)"
						autoApply
						class="input-date w-full"
						format="dd/MM/yyyy"
						minutesIncrement="30"
						placeholder="Chọn ngày"
						uid="filter-by-date"
					>
						<template #input-icon>
							<svg
								fill="none"
								height="20"
								viewBox="0 0 20 20"
								width="20"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									clip-rule="evenodd"
									d="M5 0.625C5 0.279822 4.72018 0 4.375 0C4.02982 0 3.75 0.279822 3.75 0.625V1.25H2.5C1.11929 1.25 0 2.36929 0 3.75V5H20V3.75C20 2.36929 18.8807 1.25 17.5 1.25H16.25V0.625C16.25 0.279822 15.9702 0 15.625 0C15.2798 0 15 0.279822 15 0.625V1.25H5V0.625ZM20 17.5V6.25H0V17.5C0 18.8807 1.11929 20 2.5 20H17.5C18.8807 20 20 18.8807 20 17.5ZM11.875 8.75C11.5298 8.75 11.25 9.02982 11.25 9.375V10.625C11.25 10.9702 11.5298 11.25 11.875 11.25H13.125C13.4702 11.25 13.75 10.9702 13.75 10.625V9.375C13.75 9.02982 13.4702 8.75 13.125 8.75H11.875ZM15.625 8.75C15.2798 8.75 15 9.02982 15 9.375V10.625C15 10.9702 15.2798 11.25 15.625 11.25H16.875C17.2202 11.25 17.5 10.9702 17.5 10.625V9.375C17.5 9.02982 17.2202 8.75 16.875 8.75H15.625ZM2.5 13.125C2.5 12.7798 2.77982 12.5 3.125 12.5H4.375C4.72018 12.5 5 12.7798 5 13.125V14.375C5 14.7202 4.72018 15 4.375 15H3.125C2.77982 15 2.5 14.7202 2.5 14.375V13.125ZM6.875 12.5C6.52982 12.5 6.25 12.7798 6.25 13.125V14.375C6.25 14.7202 6.52982 15 6.875 15H8.125C8.47018 15 8.75 14.7202 8.75 14.375V13.125C8.75 12.7798 8.47018 12.5 8.125 12.5H6.875Z"
									fill="#94A3B8"
									fill-rule="evenodd"
								/>
							</svg>
						</template>
					</VueDatepicker>
				</div>
			</div>
		</div>
		<div v-if="loading" class="mt-10 flex items-center justify-center h-28">
			<Loading />
		</div>
		<div v-else>
			<div v-if="startDate && endDate && !empty">
				<div class="relative mt-12">
					<div
						aria-hidden="true"
						class="absolute inset-0 flex items-center"
					>
						<div class="w-full border-t border-neutral-300" />
					</div>
					<div class="relative flex items-center justify-center">
						<div
							class="px-4 py-3 bg-white text-neutral-600 font-semibold text-sm flex items-center gap-1 cursor-pointer shadow-sm rounded-lg"
							style="border: 1px solid #f1f5f9"
							@click="handleShare"
						>
							<img
								src="@/assets/images/icons/share.png"
								alt=""
								class="w-4 h-4"
							/>
							<div>Chia sẻ</div>
						</div>
					</div>
				</div>
				<div class="mt-10">
					<div class="p-6 bg-white shadow-sm rounded-2xl">
						<div
							class="w-full flex items-center justify-between mb-6"
						>
							<div class="font-semibold text-neutral-600">
								Thống kê chung
							</div>
							<div
								class="flex items-center justify-between gap-2"
							>
								<button
									v-if="enableAiButton && !aiUsed"
									class="luca-button flex items-center gap-1"
									@click="openCreate"
								>
									<div>Tạo Đánh giá phát triển Luca AI</div>
									<img
										class="w-4 h-4"
										src="@/assets/images/icons/coin.png"
										alt=""
									/>
									<div>30</div>
								</button>
								<div>
									<Menu as="div" class="relative">
										<MenuButton
											class="rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
										>
											<span class="sr-only"
												>Open user menu</span
											>
											<svg
												fill="none"
												height="20"
												viewBox="0 0 20 20"
												width="20"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													clip-rule="evenodd"
													d="M11.875 16.25C11.875 17.2855 11.0355 18.125 10 18.125C8.96447 18.125 8.125 17.2855 8.125 16.25C8.125 15.2145 8.96447 14.375 10 14.375C11.0355 14.375 11.875 15.2145 11.875 16.25ZM11.875 10C11.875 11.0355 11.0355 11.875 10 11.875C8.96447 11.875 8.125 11.0355 8.125 10C8.125 8.96447 8.96447 8.125 10 8.125C11.0355 8.125 11.875 8.96447 11.875 10ZM11.875 3.75C11.875 4.78553 11.0355 5.625 10 5.625C8.96447 5.625 8.125 4.78553 8.125 3.75C8.125 2.71447 8.96447 1.875 10 1.875C11.0355 1.875 11.875 2.71447 11.875 3.75Z"
													fill="#94A3B8"
													fill-rule="evenodd"
												/>
											</svg>
										</MenuButton>
										<transition
											enter-active-class="transition ease-out duration-200"
											enter-from-class="transform opacity-0 scale-95"
											enter-to-class="transform opacity-100 scale-100"
											leave-active-class="transition ease-in duration-75"
											leave-from-class="transform opacity-100 scale-100"
											leave-to-class="transform opacity-0 scale-95"
										>
											<MenuItems
												class="origin-top-right absolute right-0 mt-2 w-60 rounded-md py-1 bg-white focus:outline-none z-10 shadow-sm border border-yellow-100"
											>
												<MenuItem
													v-slot="{ active }"
													@click="openPreview(true)"
												>
													<div
														:class="
															active ? '' : ''
														"
														class="p-2 px-4 bg-white z-10 text-gray-600 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
													>
														<svg
															fill="none"
															height="16"
															viewBox="0 0 16 16"
															width="16"
															xmlns="http://www.w3.org/2000/svg"
														>
															<path
																d="M13.9759 2.73814L9.31765 15.7438C9.26208 15.8975 9.1162 15.9999 8.95276 16H8.82854C8.67608 16 8.53718 15.9124 8.47141 15.7748L6.63918 11.8926C6.36938 11.3202 6.4738 10.6414 6.90314 10.1766L9.23225 7.62981C9.3752 7.47984 9.3752 7.24403 9.23225 7.09406L8.93723 6.799C8.78728 6.65604 8.55149 6.65604 8.40154 6.799L5.85504 9.12837C5.39031 9.55775 4.71155 9.66219 4.13926 9.39236L0.257408 7.55993C0.107709 7.50356 0.00662856 7.36264 0.00120576 7.20276V7.07853C-0.0125429 6.90383 0.0924328 6.74158 0.257408 6.68254L13.2616 2.0238C13.4014 1.97246 13.5583 2.00564 13.6653 2.10921L13.8672 2.31109C13.9885 2.41709 14.0318 2.58703 13.9759 2.73814Z"
																fill="#94A3B8"
															/>
														</svg>

														<span
															>Gửi tới phụ
															huynh</span
														>
													</div>
												</MenuItem>
												<MenuItem
													v-slot="{ active }"
													@click="openPreview(false)"
												>
													<div
														:class="
															active ? '' : ''
														"
														class="p-2 px-4 bg-white z-10 text-gray-600 hover:bg-neutral-50 flex items-center gap-4 cursor-pointer"
													>
														<svg
															width="16"
															height="16"
															viewBox="0 0 16 16"
															fill="none"
															xmlns="http://www.w3.org/2000/svg"
														>
															<path
																d="M9.33333 0H1.33333C0.593333 0 0 0.6 0 1.33333V10.6667C0 11.4 0.593333 12 1.33333 12H10.6667C11.4 12 12 11.4 12 10.6667V2.66667L9.33333 0ZM6 10.6667C4.89333 10.6667 4 9.77333 4 8.66667C4 7.56 4.89333 6.66667 6 6.66667C7.10667 6.66667 8 7.56 8 8.66667C8 9.77333 7.10667 10.6667 6 10.6667ZM8 4H1.33333V1.33333H8V4Z"
																fill="#94A3B8"
															/>
														</svg>
														<span
															>Lưu bản nháp</span
														>
													</div>
												</MenuItem>
											</MenuItems>
										</transition>
									</Menu>
								</div>
							</div>
						</div>
						<div class="flex flex-nowrap w-full md-down:flex-col">
							<div
								class="p-6 border rounded-l-lg border-neutral-200 w-full md-down:rounded-t md-down:rounded-b-none"
							>
								<div
									class="text-33 font-bold text-neutral-600 mb-6 flex flex-nowrap items-center"
								>
									<img
										alt=""
										class="mr-2"
										height="32"
										src="@/assets/images/icons/abc.png"
										style="height: 32px"
										width="32"
									/>
									{{
										Number(statistics?.skill_count || 0) +
										'/' +
										Number(
											data?.stats?.total_skill_count || 0
										)
									}}
								</div>
								<div class="flex flex-nowrap items-center">
									<span class="text-neutral-500 text-sm"
										>Lĩnh vực đã học
									</span>
								</div>
							</div>
							<div
								class="p-6 border-t border-b border-neutral-200 w-full md-down:border-l md-down:border-r md-down:border-b-0"
							>
								<div
									class="text-33 font-bold text-neutral-600 mb-6 flex flex-nowrap items-center"
								>
									<img
										alt=""
										class="mr-2"
										height="32"
										src="@/assets/images/icons/light.png"
										style="height: 32px"
										width="32"
									/>
									{{
										Number(statistics?.goal_count || 0) +
										'/' +
										Number(
											data?.stats?.total_goal_count || 0
										)
									}}
								</div>
								<div class="flex flex-nowrap items-center">
									<span class="text-neutral-500 text-sm"
										>Mục tiêu đã học
									</span>
								</div>
							</div>
							<div
								class="p-6 border-l border-t border-b border-neutral-200 w-full md-down:border-r md-down:border-b-0"
							>
								<div
									class="text-33 font-bold text-neutral-600 mb-6 flex flex-nowrap items-center"
								>
									<img
										alt=""
										class="mr-2"
										height="32"
										src="@/assets/images/icons/light.png"
										style="height: 32px"
										width="32"
									/>
									{{ statistics?.completed || 0 }}
								</div>
								<div class="flex flex-nowrap items-center">
									<span class="text-neutral-500 text-sm"
										>Mục tiêu đã hoàn thành
									</span>
								</div>
							</div>
							<div
								class="p-6 border-l border-t border-b border-neutral-200 w-full md-down:border-r md-down:border-b-0"
							>
								<div
									class="text-33 font-bold text-neutral-600 mb-6 flex flex-nowrap items-center"
								>
									<img
										alt=""
										class="mr-2"
										height="32"
										src="@/assets/images/icons/light.png"
										style="height: 32px"
										width="32"
									/>
									{{ statistics?.continued || 0 }}
								</div>
								<div class="flex flex-nowrap items-center">
									<span class="text-neutral-500 text-sm"
										>Mục tiêu sẽ tiếp tục
									</span>
								</div>
							</div>
							<div
								class="p-6 border rounded-r-lg border-neutral-200 w-full md-down:rounded-b md-down:rounded-t-none"
							>
								<div
									class="text-33 font-bold text-neutral-600 mb-6 flex flex-nowrap items-center"
								>
									<img
										alt=""
										class="mr-2"
										height="32"
										src="@/assets/images/icons/light.png"
										style="height: 32px"
										width="32"
									/>
									{{ statistics?.stopped || 0 }}
								</div>
								<div class="flex flex-nowrap items-center">
									<span class="text-neutral-500 text-sm"
										>Mục tiêu sẽ tạm dừng</span
									>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="mt-6" v-if="note || recommend">
					<div class="bg-white shadow-sm rounded-2xl">
						<div
							class="w-full flex items-center justify-between p-6"
							:class="{ 'border-bot': note || recommend }"
						>
							<div class="font-semibold text-neutral-600">
								Nhận xét chung về quá trình can thiệp của trẻ
							</div>
							<div v-if="editMode">
								<button
									class="flex items-center gap-2 luca-button"
									@click="editMode = false"
								>
									<img
										class="w-4 h-4"
										src="@/assets/images/icons/save.png"
										alt=""
									/>
									<div>Lưu chỉnh sửa</div>
								</button>
							</div>
							<div v-else>
								<div
									class="flex items-center gap-2 cursor-pointer"
									@click="editMode = true"
								>
									<img
										class="w-4 h-4"
										src="@/assets/images/icons/edit-fill.png"
										alt=""
									/>
									<div class="text-yellow-700 font-semibold">
										Chỉnh sửa
									</div>
								</div>
							</div>
						</div>
						<div class="p-6">
							<div class="mb-4" v-if="note">
								<div
									class="text-gray-400 font-semibold mb-1 uppercase text-xs"
								>
									NHẬN XÉT
								</div>
								<textarea
									v-if="editMode"
									rows="6"
									v-model="note"
								>
								</textarea>
								<div
									v-else
									class="text-gray-700"
									v-html="note.replaceAll('\n', '<br />')"
								></div>
							</div>
							<div v-if="recommend">
								<div
									class="text-gray-400 font-semibold mb-1 uppercase text-xs"
								>
									Gợi ý cho phụ huynh
								</div>
								<textarea
									v-if="editMode"
									rows="6"
									v-model="recommend"
								>
								</textarea>
								<div
									v-else
									class="text-gray-700"
									v-html="
										recommend.replaceAll('\n', '<br />')
									"
								></div>
							</div>
						</div>
					</div>
				</div>
				<div class="mt-6" v-if="skillsWithAge.length > 0">
					<div class="bg-white shadow border-sm rounded-lg">
						<div class="border-bot">
							<div
								class="text-neutral-500 font-semibold border-b p-6"
							>
								Biểu đồ mối tương quan tuổi phát triển và tuổi
								thực của trẻ qua các lĩnh vực đã học
							</div>
							<div
								class="font-semibold text-gray-700 whitespace-nowrap px-6 pt-6 mb-2"
							>
								Lĩnh vực
							</div>
							<div
								class="relative px-6 pb-6 flex items-center text-neutral-500 gap-4 overflow-x-auto"
							>
								<div class="h-full pb-9">
									<div
										:style="{
											height:
												45 * skillsWithAge.length +
												'px',
										}"
										class="flex flex-col items-start justify-between"
									>
										<div
											v-for="i in skillsWithAge"
											:key="i.id"
											class="font-semibold text-gray-400 whitespace-nowrap"
										>
											{{ i.name }}
										</div>
									</div>
								</div>
								<div
									class="w-max flex flex-nowrap pt-6 relative"
								>
									<div
										class="absolute l-0 w-full flex flex-col items-start justify-between"
										:style="{
											height:
												45 * skillsWithAge.length +
												'px',
										}"
									>
										<div
											v-for="i in skillsWithAge"
											:key="i.id"
											class="flex items-center gap-2 w-full"
										>
											<div
												:style="{
													width: getAgeWidth(
														i.unit === 'tuổi'
															? i.age * 12
															: i.age
													),
												}"
												class="percent"
											></div>
											<div
												class="text-gray-500 font-semibold text-sm"
											>
												{{
													i.unit === 'tuổi'
														? i.age * 12
														: i.age
												}}
											</div>
										</div>
									</div>
									<div
										v-for="i in formattedValues"
										:key="i"
										class="flex-1 w-16"
									>
										<div
											class="border border-t-0 border-b-0 border-l border-neutral-200"
											:class="{
												'border-r-0':
													i !==
													formattedValues.length - 1,
											}"
											:style="{
												height:
													45 * skillsWithAge.length +
													'px',
											}"
										></div>
										<div
											class="text-neutral-500 text-sm mt-8 font-semibold float-left -ml-2"
										>
											{{ i }}
										</div>
									</div>
									<div
										class="absolute text-gray-500 font-semibold whitespace-nowrap"
										:style="{
											left: getAgeWidth(
												parseAgeToMonths(
													student?.student?.age
												)
											),
											'margin-left': '-25px',
											top: '-5px',
										}"
									>
										{{ student?.student?.age }}
									</div>
									<div
										class="absolute student-age"
										:style="{
											left: getAgeWidth(
												parseAgeToMonths(
													student?.student?.age
												)
											),
											height:
												45 * skillsWithAge.length +
												'px',
										}"
									></div>
								</div>
								<div
									class="flex items-end font-semibold text-gray-700 whitespace-nowrap"
									:style="{
										height:
											45 * skillsWithAge.length +
											81 +
											'px',
									}"
								>
									Tuổi phát triển
								</div>
							</div>
							<div
								class="text-gray-700 flex item-center gap-10 p-6"
							>
								<div class="flex items-center gap-2">
									<div class="r-age w-4 h-4 rounded-lg"></div>
									<div class="text-sm">Tuổi thực</div>
								</div>
								<div class="flex items-center gap-2">
									<div class="d-age w-4 h-4 rounded-lg"></div>
									<div class="text-sm">Tuổi phát triển</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="mt-6 flex items-stretch gap-6 xl-down:flex-col">
					<div
						class="bg-white shadow-sm rounded-2xl overflow-hidden w-1/3 xl-down:w-full"
					>
						<div class="font-semibold text-neutral-500 p-6">
							Biều đồ cảm xúc
						</div>
						<div
							class="border-t border-orange-50 flex justify-center items-center p-6 flex-col"
						>
							<div
								class="relative w-full md-down:w-full flex items-center justify-center"
							>
								<apexchart
									:options="chartOptions3"
									:series="series3"
									height="220"
									type="donut"
								></apexchart>
								<div
									class="text-center absolute top-1/2 left-1/2 transform"
									style="transform: translate(-50%, -50%)"
								>
									<div class="text-neutral-500">Cảm xúc</div>
								</div>
							</div>
							<div class="w-full">
								<div
									v-if="series3[0] > 0"
									class="flex flex-nowrap items-center justify-between w-full"
								>
									<div class="flex flex-nowrap items-center">
										<img
											alt=""
											height="24"
											src="@/assets/images/emoji/happy.png"
											width="24"
										/>
										<div
											class="h-2.5 w-2.5 rounded-full ml-2"
											style="background: #9edf93"
										></div>
										<div
											class="ml-2 text-base text-neutral-500"
										>
											Vui vẻ
										</div>
									</div>
									<div
										class="text-base text-neutral-600 font-bold"
									>
										{{ series3[0] }}
										%
									</div>
								</div>
								<div
									v-if="series3[1] > 0"
									class="flex flex-nowrap items-center justify-between w-full mt-2.5"
								>
									<div class="flex flex-nowrap items-center">
										<img
											alt=""
											height="24"
											src="@/assets/images/emoji/5565d66a50b44df9ae37a955bdfb3757.png"
											width="24"
										/>
										<div
											class="h-2.5 w-2.5 rounded-full ml-2"
											style="background: #d0db54"
										></div>
										<div
											class="ml-2 text-base text-neutral-500"
										>
											Bình thường
										</div>
									</div>
									<div
										class="text-base text-neutral-600 font-bold"
									>
										{{ series3[1] }}
										%
									</div>
								</div>
								<div
									v-if="series3[2] > 0"
									class="flex flex-nowrap items-center justify-between w-full mt-2.5"
								>
									<div class="flex flex-nowrap items-center">
										<img
											alt=""
											height="24"
											src="@/assets/images/emoji/cf1f643bc1244f5e8e37767bc946d607.png"
											width="24"
										/>
										<div
											class="h-2.5 w-2.5 rounded-full ml-2"
											style="background: #ffe68c"
										></div>
										<div
											class="ml-2 text-base text-neutral-500"
										>
											Khó chịu
										</div>
									</div>
									<div
										class="text-base text-neutral-600 font-bold"
									>
										{{ series3[2] }}
										%
									</div>
								</div>
								<div
									v-if="series3[3] > 0"
									class="flex flex-nowrap items-center justify-between w-full mt-2.5"
								>
									<div class="flex flex-nowrap items-center">
										<img
											alt=""
											height="24"
											src="@/assets/images/emoji/5ede936757cd4fbdb6247bad22bf454d.png"
											width="24"
										/>
										<div
											class="h-2.5 w-2.5 rounded-full ml-2"
											style="background: #ff9d6d"
										></div>
										<div
											class="ml-2 text-base text-neutral-500"
										>
											Tức giận
										</div>
									</div>
									<div
										class="text-base text-neutral-600 font-bold"
									>
										{{ series3[3] }}
										%
									</div>
								</div>
								<div
									v-if="series3[4] > 0"
									class="flex flex-nowrap items-center justify-between w-full mt-2.5"
								>
									<div class="flex flex-nowrap items-center">
										<img
											alt=""
											height="24"
											src="@/assets/images/emoji/0b356bb507d64b9489e2fbced131f795.png"
											width="24"
										/>
										<div
											class="h-2.5 w-2.5 rounded-full ml-2"
											style="background: #ff6967"
										></div>
										<div
											class="ml-2 text-base text-neutral-500"
										>
											Bùng nổ
										</div>
									</div>
									<div
										class="text-base text-neutral-600 font-bold"
									>
										{{ series3[4] }}
										%
									</div>
								</div>
							</div>
						</div>
					</div>
					<div
						class="bg-white shadow-sm rounded-2xl overflow-hidden w-1/3 xl-down:w-full xl-down:mt-6"
					>
						<div class="font-semibold text-neutral-500 p-6">
							Biểu đồ kết quả các mục tiêu
						</div>
						<div
							class="flex flex-nowrap p-6 border-t border-orange-50 items-center justify-center flex-col"
						>
							<div class="relative w-fit md-down:w-full">
								<apexchart
									:options="chartOptions"
									:series="series"
									height="220"
									type="donut"
								></apexchart>
								<div
									class="text-center absolute top-1/2 left-1/2 transform"
									style="transform: translate(-50%, -50%)"
								>
									<div
										class="text-4xl font-bold text-neutral-600"
									>
										{{ statistics?.goal_count }}
									</div>
									<div class="text-neutral-500">Mục tiêu</div>
								</div>
							</div>
							<div
								class="flex flex-col w-full justify-center md-down:mt-4"
							>
								<div
									v-if="result[0]?.count > 0"
									class="flex flex-nowrap items-center mb-4 justify-between"
								>
									<div
										class="text-sm font-semibold text-neutral-500 w-14 blue"
									>
										{{ result[0]?.count }}
										M.T
									</div>
									<span
										class="flex items-center justify-center py-0.5 rounded-full text-sm text-white font-semibold whitespace-nowrap w-32 shadow-input"
										style="background: #2aa797"
									>
										{{ result[0]?.result }}
									</span>
								</div>
								<div
									v-if="result[1]?.count > 0"
									class="flex flex-nowrap items-center mb-4 justify-between"
								>
									<div
										class="text-sm text-neutral-500 w-14 font-semibold"
									>
										{{ result[1]?.count }}
										M.T
									</div>
									<span
										class="flex items-center justify-center py-0.5 rounded-full text-sm text-white font-semibold w-32 whitespace-nowrap shadow-input"
										style="background: #65a30d"
									>
										{{ result[1]?.result }}
									</span>
								</div>
								<div
									v-if="result[2]?.count > 0"
									class="flex flex-nowrap items-center mb-4 justify-between"
								>
									<div
										class="text-sm font-semibold text-neutral-500 w-14"
									>
										{{ result[2]?.count }}
										M.T
									</div>
									<span
										class="flex items-center justify-center py-0.5 rounded-full text-sm text-white font-semibold w-32 whitespace-nowrap shadow-input"
										style="background: #f59e0b"
									>
										{{ result[2]?.result }}
									</span>
								</div>
								<div
									v-if="result[3]?.count > 0"
									class="flex flex-nowrap items-center mb-4 justify-between"
								>
									<div
										class="text-sm font-semibold text-neutral-500 w-14 blue"
									>
										{{ result[3]?.count }}
										M.T
									</div>
									<span
										class="flex items-center justify-center px-3 py-0.5 rounded-full text-sm text-white font-semibold w-32 whitespace-nowrap shadow-input"
										style="background: #f87171"
									>
										{{ result[3]?.result }}
									</span>
								</div>
								<div
									v-if="
										result[4]?.count > 0 ||
										result[5]?.count > 0 ||
										result[6]?.count > 0
									"
									class="flex flex-nowrap items-center mb-4 justify-between"
								>
									<div
										class="text-sm font-semibold text-neutral-500 w-14 blue"
									>
										{{
											result[4]?.count +
											result[5]?.count +
											result[6]?.count
										}}
										M.T
									</div>
									<span
										class="flex items-center justify-center px-3 py-0.5 rounded-full text-sm text-white font-semibold w-32 whitespace-nowrap shadow-input"
										style="background: #9ca3af"
									>
										X
									</span>
								</div>
							</div>
						</div>
					</div>
					<div
						class="bg-white shadow-sm rounded-2xl overflow-hidden w-1/3 xl-down:w-full xl-down:mt-6"
					>
						<div class="font-semibold text-neutral-500 p-6">
							Chuyên cần
						</div>
						<div
							class="border-t border-orange-50 p-6 flex items-center justify-center flex-col"
						>
							<div
								class="text-neutral-600 font-semibold mb-5"
								style="font-size: 72px"
							>
								{{ data?.stats?.attendance }}
							</div>
							<div class="text-neutral-500 mb-5 text-center">
								Số ngày học
							</div>
						</div>
					</div>
				</div>
				<div class="mt-6">
					<div
						class="bg-white shadow-sm rounded-2xl overflow-hidden mb-10 pb-5"
					>
						<div class="font-semibold text-neutral-500 p-6">
							Mối tương quan giữa kết quả, cảm xúc và sự tham gia
							của trẻ từ
							{{ formatDate(startDate, true) }} tới
							{{ formatDate(endDate, true) }}
						</div>
						<div
							class="p-6 border-t border-orange-50 md-down:px-0 overflow-x-auto w-full"
						>
							<apexchart
								ref="mixChart"
								:options="mixChartOptions"
								:series="emotion_cooperation_result_by_day_data"
								:style="{
									minWidth: `${
										emotion_cooperation_result_by_day_emotion?.length *
										150
									}px`,
								}"
								height="600"
								style="width: 100%"
								width="100%"
							></apexchart>
						</div>
					</div>
				</div>
				<div
					v-if="radarData.length && radarData[0]?.data.length >= 3"
					class="mt-12"
				>
					<div
						class="bg-white shadow-sm overflow-hidden rounded-2xl mb-10 pb-5"
					>
						<div class="font-semibold text-neutral-500 p-6">
							Biểu đồ mối tương quan giữa kết quả các lĩnh vực từ
							{{ formatDate(startDate, true) }} tới
							{{ formatDate(endDate, true) }}
						</div>
						<div
							class="p-6 border-t border-orange-50 md-down:px-0 overflow-x-auto w-full"
						>
							<apexchart
								:options="radarChartOptions"
								:series="radarData"
								height="700"
								type="radar"
							></apexchart>
							<div
								class="py-4 flex flex-wrap gap-6 justify-center items-center"
							>
								<div
									v-for="(c, index) in categories"
									:key="c"
									class="flex items-center flex-nowrap gap-2"
								>
									<div
										class="font-bold text-lf text-neutral-700"
									>
										{{ index + 1 }}: {{ c }}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="relative mt-10">
					<div
						aria-hidden="true"
						class="absolute inset-0 flex items-center"
					>
						<div class="w-full border-t border-neutral-300" />
					</div>
					<div class="relative flex items-center justify-center">
						<div
							class="px-2.5 py-1 bg-body text-neutral-600 font-semibold"
						>
							Đánh giá về các lĩnh vực đã học
						</div>
					</div>
				</div>
				<div
					v-for="(s, index) in skills.length > 0
						? skills
						: assessment_results"
					:key="index"
					class="mt-10"
					:class="{
						block: skills.length > 0,
						'shadow-sm': skills.length > 0,
					}"
				>
					<div class="flex items-center justify-between mb-4">
						<div class="flex items-center">
							<div class="font-semibold text-neutral-700">
								{{ index + 1 }}. {{ s.skill?.name }}
							</div>
							<div class="flex items-center gap-2" v-if="s.age">
								<div>:</div>
								<div class="font-semibold text-green-500">
									{{ s.age }} {{ s.unit }}
								</div>
							</div>
						</div>
						<div v-if="editGoal && goalIndex === index">
							<button
								class="flex items-center gap-2 luca-button"
								@click="save"
							>
								<img
									class="w-4 h-4"
									src="@/assets/images/icons/save.png"
									alt=""
								/>
								<div>Lưu chỉnh sửa</div>
							</button>
						</div>
						<div
							v-else
							class="flex items-center gap-2 cursor-pointer"
							@click="edit(index)"
						>
							<img
								class="w-4 h-4"
								src="@/assets/images/icons/edit-fill.png"
								alt=""
							/>
							<div class="text-yellow-700 font-semibold">
								Chỉnh sửa
							</div>
						</div>
					</div>
					<div v-if="skills.length > 0">
						<div
							class="text-xs font-semibold uppercase mb-2 text-gray-400"
							v-if="s.age || (editGoal && goalIndex === index)"
						>
							Tuổi phát triẻn
						</div>
						<div class="mb-4">
							<div
								v-if="editGoal && goalIndex === index"
								class="flex items-center gap-2"
							>
								<div class="input-group" style="width: 100px">
									<input type="number" v-model="s.age" />
								</div>
								<Multiselect
									style="width: 200px"
									:options="unitType"
									v-model="s.unit"
									:allow-empty="false"
									placeholder="Chọn loại tuổi"
									deselect-label=""
									selectLabel=""
								>
								</Multiselect>
							</div>
							<div v-else class="text-gray-700">
								<span v-if="s.age"
									>{{ s.age }} {{ s.unit }}</span
								>
							</div>
						</div>
						<div class="mb-4">
							<div
								class="text-xs font-semibold uppercase mb-2 text-gray-400"
								v-if="
									s.note || (editGoal && goalIndex === index)
								"
							>
								Nhận xét chung
							</div>
							<textarea
								v-if="editGoal && goalIndex === index"
								rows="6"
								v-model="s.note"
							>
							</textarea>
							<div
								v-else
								class="text-gray-700"
								v-html="s.note"
							></div>
						</div>
						<div class="mb-4">
							<div
								class="text-xs font-semibold uppercase mb-2 text-gray-400"
								v-if="
									s.recomended_for_parent ||
									(editGoal && goalIndex === index)
								"
							>
								Gợi ý cho phụ huynh
							</div>
							<textarea
								v-if="editGoal && goalIndex === index"
								rows="6"
								v-model="s.recomended_for_parent"
							>
							</textarea>
							<div
								v-else
								class="text-gray-700"
								v-html="s.recomended_for_parent"
							></div>
						</div>
						<div
							class="text-xs font-semibold uppercase mb-2 text-gray-400"
						>
							Các mục tiêu đã học
						</div>
					</div>
					<div
						class="shadow-sm sm:rounded-2xl overflow-hidden md-down:hidden"
					>
						<div class="__record__table overflow-x-auto scroll-bar">
							<div
								class="overflow-hidden sm:rounded-t-2xl w-fit min-w-full"
							>
								<table
									class="min-w-full divide-y text-neutral-200"
								>
									<thead class="bg-neutral-100 h-16">
										<tr>
											<th
												class="px-4 py-3 text-center text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap w-16"
												scope="col"
											>
												STT
											</th>
											<th
												class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
												scope="col"
											>
												Mục Tiêu
											</th>
											<th
												class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
												scope="col"
												style="width: 80px"
											>
												Kết quả
											</th>
											<th
												class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 tracking-wider whitespace-nowrap"
												scope="col"
												style="width: 140px"
												v-if="
													editGoal &&
													goalIndex === index
												"
											>
												<div
													class="flex items-center gap-1"
												>
													<div class="uppercase">
														Hiển thị MT
													</div>
													<Popper
														placement="top"
														hover
														:arrow="true"
													>
														<img
															class="cursor-pointer"
															src="@/assets/images/icons/info-circle-fill.png"
															alt=""
														/>

														<template #content>
															<div
																style="
																	max-width: 276px;
																"
																class="text-lg whitespace-normal text-neutral-600 font-normal"
															>
																Khi tắt, mục
																tiêu sẽ không
																được hiển thị
																trên báo cáo can
																thiệp định kỳ
																của trẻ.
															</div>
														</template>
													</Popper>
												</div>
											</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="(i, gIndex) in s.goals"
											:key="gIndex"
											:class="[
												gIndex % 2 === 0
													? 'bg-white'
													: 'bg-neutral-50',
												i.show && !editGoal
													? 'opacity-30'
													: '',
											]"
											class="cursor-pointer text-neutral-700 hover:bg-orange-50"
											@click="
												openGoalDetail(
													i.goal_id || i.id
												)
											"
										>
											<td
												class="p-4 whitespace-nowrap text-neutral-700 text-center"
											>
												{{ gIndex + 1 }}
											</td>
											<td
												class="px-6 py-4 whitespace-nowrap text-neutral-700"
											>
												<div
													class="flex items-center gap-2"
												>
													<div
														class="hover:text-yellow-700 hover:underline whitespace-normal"
														v-html="
															i.name ||
															i.goal_name
														"
													></div>
													<TooltipGoalDetail
														:goal-id="
															i.goal_id || i.id
														"
														:student-id="studentId"
													/>
												</div>
												<div
													class="flex gap-2 flex-wrap"
													style="max-width: 360px"
												>
													<div
														class="mt-1 py-0.5 px-2.5 rounded-full bg-neutral-200 text-neutral-700 h-fit w-fit font-semibold shadow-input whitespace-nowrap"
														style="font-size: 10px"
														v-if="i?.source"
													>
														{{ i?.source }}
													</div>
													<div
														class="mt-1 py-0.5 px-2.5 rounded-full bg-neutral-200 text-neutral-700 h-fit w-fit font-semibold shadow-input whitespace-nowrap"
														style="font-size: 10px"
														v-else
													>
														{{
															$filters.sourceMap(
																i?.state,
																i.editable
															)
														}}
													</div>
													<template
														v-if="
															i?.assistance_levels
														"
													>
														<div
															v-for="ass in i?.assistance_levels"
															:key="ass"
															class="flex gap-2"
														>
															<div
																class="mt-1 py-0.5 px-2.5 rounded-full bg-pink-100 whitespace-nowrap text-neutral-700 h-fit w-fit font-semibold shadow-input"
																style="
																	font-size: 10px;
																"
															>
																{{ ass }}
															</div>
														</div>
													</template>
													<div
														class="mt-1 py-0.5 px-2.5 rounded-full bg-yellow-tag text-neutral-700 h-fit w-fit font-semibold shadow-input whitespace-nowrap"
														style="font-size: 10px"
														v-if="i?.age"
													>
														{{ i?.age }}
													</div>
												</div>
												<div
													class="flex items-center gap-2 mt-2 text-neutral-400 text-xs font-semibold"
												>
													<div v-if="i?.has_media">
														<img
															alt=""
															src="@/assets/images/icons/has_media.png"
														/>
													</div>
													<div
														v-if="
															i.number_of_assessment >
															0
														"
													>
														ĐÃ HỌC
														{{
															i.number_of_assessment
														}}
														NGÀY
													</div>
													<div
														v-if="
															i.display_start_date
														"
													>
														|
													</div>
													<div
														v-if="
															i.display_start_date
														"
													>
														{{
															i.display_start_date
														}}
													</div>
													<div
														v-if="
															i.display_completed_at
														"
													>
														-
													</div>
													<div
														v-if="
															i.display_completed_at
														"
													>
														{{
															i.display_completed_at
														}}
													</div>
													<div
														v-if="
															i?.display_completed_at
														"
													>
														<img
															alt=""
															src="@/assets/images/icons/has_taught.png"
														/>
													</div>
												</div>
												<div
													v-if="
														i.parent
															?.sub_goal_count > 0
													"
													class="goal-block flex gap-3 mt-2"
												>
													<img
														class="w-3 h-3 mt-1"
														src="@/assets/images/icons/goal-arrow.png"
														alt=""
													/>
													<div>
														<div
															class="text-neutral-700 whitespace-normal"
															v-html="
																i.parent?.name
															"
														></div>
														<div
															class="text-neutral-400 text-xs font-semibold"
														>
															{{
																i.parent
																	?.sub_goal_count
															}}
															MỤC TIÊU
														</div>
													</div>
												</div>
											</td>
											<td
												class="px-6 py-4 whitespace-nowrap text-neutral-700"
											>
												<div>
													<div
														v-if="
															i?.complete_percent
														"
														:style="{
															background:
																$filters.tagResult(
																	i?.complete_percent
																)?.background,
															color: $filters.tagResult(
																i?.complete_percent
															)?.color,
														}"
														class="py-0.5 px-2.5 rounded-full text-neutral-700 h-fit w-fit font-semibold text-sm shadow-input"
													>
														Đạt
														{{
															i?.complete_percent ||
															0
														}}%
													</div>
												</div>
											</td>
											<td
												v-if="
													editGoal &&
													goalIndex === index
												"
												class="px-6 py-4 whitespace-nowrap text-neutral-700"
											>
												<div
													class="flex items-center justify-center"
													@click.stop="
														i.show = !i.show
													"
												>
													<img
														v-if="!i.show"
														src="@/assets/images/icons/eye-yellow.png"
													/>
													<img
														v-else
														src="@/assets/images/icons/eye-slash-fill.png"
													/>
												</div>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-else class="mt-10 flex items-center flex-col justify-center">
				<img
					v-if="empty"
					alt=""
					class="process-empty-art"
					src="@/assets/images/illustrations/screening-empty.svg"
				/>
				<img
					v-else
					alt=""
					height="250"
					src="@/assets/images/illustrations/not_found.png"
					width="250"
				/>
				<div
					class="text-neutral-500 text-lg text-center"
					style="max-width: 700px"
				>
					<span v-if="!startDate || !endDate"
						>Vui lòng chọn khoảng thời gian cần xem thống kê kết quả
						can thiệp</span
					>
					<span v-else-if="empty"
						>Học sinh chưa có Kết quả can thiệp trong giai đoạn
						này</span
					>
				</div>
			</div>
		</div>
	</div>
</template>
<style lang="scss" scoped>
@import '../../../../../assets/styles/responsive';

.is-MT-show {
	width: 100vw;
	transform: translateX(0);
	transition: all 0.4s ease;

	@include media-breakpoint-up(lg) {
		width: 400px;
	}
}

.is-MT-hide {
	transform: translateX(450px);
	transition: all 0.4s ease;
}

.transition {
	transition: all 0.4s ease;
}

.content-report-is-show {
	@include media-breakpoint-up(lg) {
		padding-right: 448px;
	}
}

.content-report-is-hide {
	@include media-breakpoint-up(lg) {
		padding-right: 40px;
	}
}
.process-empty-art {
	width: 220px;
	height: auto;
	transform: rotate(180deg) scaleX(-1);
}

.border-bot {
	border-bottom: 2px solid #faf5f1;
}
.percent {
	height: 24px;
	border-radius: 0 99px 99px 0;
	background: #f9a8d4;
}
.student-age {
	border: 3px dashed #86a8d0;
}
.r-age {
	background: #6390c3;
}
.d-age {
	background: #f9a8d4;
}
.student-age::before,
.student-age::after {
	content: '';
	position: absolute;
	left: -7.5px;
	width: 15px;
	height: 15px;
	background-color: #6390c3;
	border-radius: 50%;
}

.student-age::before {
	top: -10px;
}

.student-age::after {
	bottom: -10px;
}
.goal {
	border-radius: 8px;
	padding: 24px;
	background: #ffffff;
	border: none !important;
}
.opacity-30 {
	opacity: 0.3 !important;
}
.block {
	border-radius: 8px;
	padding: 24px;
	background: #ffffff;
	border: none !important;
}
</style>
